import styled from '@emotion/styled';
import { Button, Form, Modal, Spinner, Stack } from 'react-bootstrap';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import CustomerAutocomplete from 'app/components/CustomerAutocomplete';
import { formatCurrency } from 'app/utils/formatCurrency';

import { InvoiceLinesForm } from './InvoiceLineForm';
import { InvoiceFormProps } from './InvoiceModal.types';

// Styled Emotion components

const ModalBodyStyled = styled(Modal.Body)`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormGroupThird = styled(Form.Group)`
  width: calc(33.33% - 0.67rem);
`;

const HorizontalStack = styled(Stack)`
  align-items: flex-start;
  flex-direction: row !important;
  gap: 1rem;
  width: 100%;
  justify-content: space-between;
`;

const DatePickerWrapper = styled.div`
  /* Ensures datepicker input fills container width */
  width: 100%;
`;

const TotalAmount = styled.div`
  width: 150px;
  font-weight: 600;
`;

export interface InvoiceModalProps {
  onSubmit: (data: InvoiceFormProps) => void;
  onClose: (id?: number) => void;
  title: string;
  saveAction: string;
  defaultValues?: InvoiceFormProps;
  disabledFields?: ('customer' | 'date' | 'deadline')[];
  isSubmitting?: boolean;
}

const InvoiceModal = ({
  onSubmit,
  onClose,
  title,
  saveAction,
  defaultValues,
  disabledFields,
  isSubmitting = false,
}: InvoiceModalProps) => {
  const formMethods = useForm<InvoiceFormProps>({
    defaultValues,
  });

  const {
    handleSubmit,
    control,
    formState: { errors },
    watch,
    setValue,
  } = formMethods;

  const date = watch('date');
  const deadline = watch('deadline');

  const total =
    watch('invoiceLines')?.reduce(
      (acc, line) =>
        acc + (Number(line.product?.unit_price) ?? 0) * Number(line.quantity),
      0
    ) ?? 0;

  return (
    <Modal show onHide={() => onClose()} animation={false} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>

      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalBodyStyled>
            <HorizontalStack>
              <FormGroupThird controlId="customer">
                <Form.Label>Customer</Form.Label>
                <Controller
                  name="customer"
                  control={control}
                  render={({ field: { onChange, value, ref } }) => (
                    <CustomerAutocomplete
                      ref={ref}
                      value={value}
                      onChange={onChange}
                      error={errors.customer?.message}
                      isDisabled={disabledFields?.includes('customer')}
                      inputId="customer"
                    />
                  )}
                  rules={{ required: 'Select a customer' }}
                />
              </FormGroupThird>

              <FormGroupThird controlId="date">
                <Form.Label>Date</Form.Label>
                <DatePickerWrapper>
                  <ReactDatePicker
                    dateFormat="dd/MM/yyyy"
                    selected={date ? new Date(date) : null}
                    onChange={(d) => setValue('date', d?.toString() ?? '')}
                    customInput={<Form.Control />}
                  />
                </DatePickerWrapper>
              </FormGroupThird>

              <FormGroupThird controlId="deadline">
                <Form.Label>Deadline</Form.Label>
                <DatePickerWrapper>
                  <ReactDatePicker
                    dateFormat="dd/MM/yyyy"
                    selected={deadline ? new Date(deadline) : null}
                    onChange={(d) => setValue('deadline', d?.toString() ?? '')}
                    customInput={<Form.Control />}
                    isClearable
                  />
                </DatePickerWrapper>
              </FormGroupThird>
            </HorizontalStack>

            <div>
              <strong>Invoice items</strong>
            </div>

            <InvoiceLinesForm />
          </ModalBodyStyled>

          <Modal.Footer>
            <TotalAmount>Total: {formatCurrency(total)}</TotalAmount>
            <Button variant="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting && (
                <Spinner animation="border" size="sm" className="me-2" />
              )}
              {saveAction}
            </Button>
          </Modal.Footer>
        </form>
      </FormProvider>
    </Modal>
  );
};

export default InvoiceModal;
